import React, { useState } from 'react';

export default function PurposeValuesSection() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [expandedImage, setExpandedImage] = useState(null);

  const values = ['Precision', 'Innovation', 'Speed', 'Growth', 'Vision'];

  const missionItems = [
    'AI-Driven Innovation',
    'Precision & Advanced Technology',
    'Cloud-First Solutions',
    'Legendary Speed & Excellence',
    'Victory Through Technology'
  ];

  return (
    <section className="relative bg-gray-50 py-24 lg:py-32 overflow-hidden border-b border-gray-200">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[150px] pointer-events-none z-0"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[150px] pointer-events-none z-0"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        {/* Section Header & Core Values */}
        <div className="text-center mb-16 lg:mb-20">
          <div className="inline-flex items-center border border-gray-200 px-4 py-2 bg-white/80 backdrop-blur-sm text-xs font-bold uppercase tracking-widest text-amber-600 mb-6 rounded-md shadow-sm">
            <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 animate-pulse"></span>
            Wings of Innovation
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight uppercase mb-8 text-gray-900">
            Rise on the <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-amber-700">Wings of Change</span>
          </h2>

          {/* Values Bar */}
          <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-4 lg:gap-6 text-[10px] sm:text-xs lg:text-sm font-bold uppercase tracking-widest text-gray-500">
            {values.map((value, index) => (
              <React.Fragment key={index}>
                <span className="text-gray-800 whitespace-nowrap">{value}</span>
                {index < values.length - 1 && <span className="text-amber-500">•</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Two Columns (Vision & Mission) */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          
          {/* VISION CARD (Light) */}
          <div
            className="relative bg-white rounded-[2rem] p-8 sm:p-12 shadow-xl border border-gray-100 overflow-hidden group hover:border-amber-500/40 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 flex flex-col"
            onMouseEnter={() => setHoveredCard('vision')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl -mr-20 -mt-20 transition-all duration-500 group-hover:bg-amber-500/15"></div>

            <div className="w-14 h-14 bg-gray-900 text-amber-500 rounded-2xl flex items-center justify-center mb-8 shadow-md group-hover:scale-110 transition-transform duration-500">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
              </svg>
            </div>

            <h3 className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-3">Our Vision</h3>
            <h4 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-6 leading-tight">
              Architect an Invincible Digital Universe
            </h4>

            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-8 flex-grow">
              To architect an invincible digital universe where the sharp vision of a Garuda meets the limitless possibilities of the cloud. It is our ultimate dream to become the heartbeat of the modern economy—a world where businesses don't just adapt to technology, but use it to rule their own skies with absolute confidence and legendary speed.
            </p>

            {/* Embedded Cinematic Image */}
            <div className="w-full h-48 sm:h-56 rounded-xl overflow-hidden border border-gray-100 relative mt-auto">
              <img
                src="/img/vision1.png"
                alt="Abstract image representing Garud Stacks' vision for cloud innovation"
                onClick={() => setExpandedImage('/img/vision1.png')}
                className="w-full h-full object-cover transform transition-all duration-1000 group-hover:scale-110 cursor-pointer hover:shadow-lg"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent pointer-events-none"></div>
              <div className="absolute bottom-4 left-5 text-white text-sm font-bold uppercase tracking-wider flex items-center gap-2 pointer-events-none">
                <span className="w-4 h-[2px] bg-amber-500"></span> Cloud Innovation Universe
              </div>
            </div>
          </div>

          {/* MISSION CARD (Dark) */}
          <div
            className="relative bg-gray-900 text-white rounded-[2rem] p-8 sm:p-12 shadow-2xl border border-gray-800 overflow-hidden group hover:border-amber-500/50 transition-all duration-500 hover:shadow-[0_20px_40px_-15px_rgba(245,158,11,0.3)] hover:-translate-y-2 flex flex-col"
            onMouseEnter={() => setHoveredCard('mission')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl -mr-20 -mt-20 transition-all duration-500 group-hover:bg-amber-500/20"></div>

            <div className="w-14 h-14 bg-amber-500 text-gray-900 rounded-2xl flex items-center justify-center mb-8 shadow-md group-hover:scale-110 transition-transform duration-500">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>

            <h3 className="text-xs font-bold uppercase tracking-widest text-amber-400 mb-3">Our Mission</h3>
            <h4 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-6 leading-tight">
              Empower Your Business with AI-Driven Innovation
            </h4>

            <p className="text-gray-400 text-base sm:text-lg leading-relaxed mb-10">
              At Garud Stacks, we empower your business with the wings of AI-driven innovation. Guided by the sharp eye of precision and the straight power of advanced technology, our mission unfolds as we transform your digital journey into a flight of unparalleled success. We don't just provide services, we bring the majestic speed of the Garuda to your vision, ensuring every ascent ends in victory.
            </p>

            {/* Mission Items */}
            <ul className="space-y-5 mt-auto">
              {missionItems.map((item, index) => (
                <li key={index} className="flex items-center gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 text-amber-400 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-gray-900 transition-colors duration-300">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </span>
                  <span className="text-gray-200 font-medium tracking-wide sm:text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Expanded Image Modal */}
        {expandedImage && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setExpandedImage(null)}
          >
            <div 
              className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={expandedImage}
                alt="Expanded view"
                className="w-full h-full object-contain"
              />
              <button
                onClick={() => setExpandedImage(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors duration-300 backdrop-blur-sm"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
